import { Show } from 'solid-js'
import type { HubCheckIn } from '../../lib/hub'

/**
 * The arrival / venue-link state — the hub's answer to "where am I?"
 *
 * The hub is not a static token generator: a guest is either *arriving* (not yet
 * linked to a venue, so the card is the entry point to the counter) or *checked
 * in* (linked, and the card confirms that link instead of offering to start over).
 *
 * Without this, the hub would offer "Check in" to someone who already is, and the
 * only way to discover an active session would be to scroll to another card.
 */
export default function VenueActionCard(props: {
  activeCheckIns: HubCheckIn[]
  onCheckIn: () => void
}) {
  const linked = () => props.activeCheckIns.length > 0
  const primary = () => props.activeCheckIns[0]
  /** More than one open session means the guest is at two venues. */
  const multiple = () => props.activeCheckIns.length > 1

  return (
    <section
      class={`rounded-2xl border p-5 ${
        linked() ? 'bg-white/5 border-emerald-400/40' : 'bg-emerald-500 text-emerald-950 border-transparent'
      }`}
    >
      <Show
        when={linked()}
        fallback={
          <>
            <h2 class="text-base font-bold">At a venue?</h2>
            <p class="text-sm text-emerald-900/80 mt-1">
              Scan the venue's QR code, or pick it from the list, to start a check-in.
            </p>
            <button
              type="button"
              onClick={() => props.onCheckIn()}
              class="mt-4 w-full py-3 rounded-xl bg-emerald-950 text-white font-semibold
                     hover:bg-black active:bg-black transition-colors"
            >
              Check in items
            </button>
          </>
        }
      >
        <div class="flex items-center gap-2">
          <span class="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
          <h2 class="text-sm font-semibold text-emerald-200">Linked to a venue</h2>
        </div>
        <p class="mt-2 text-white font-semibold">{primary()?.venueName}</p>
        <p class="text-xs text-white/50 mt-0.5">
          {primary()?.items.length ?? 0} item{(primary()?.items.length ?? 0) === 1 ? '' : 's'} in their care
          <Show when={multiple()}>
            {' '}
            · plus {props.activeCheckIns.length - 1} other venue
            {props.activeCheckIns.length - 1 === 1 ? '' : 's'}
          </Show>
        </p>
        <button
          type="button"
          onClick={() => props.onCheckIn()}
          class="mt-4 w-full py-3 rounded-xl bg-white/10 text-white font-medium
                 hover:bg-white/15 transition-colors"
        >
          Check in another item
        </button>
      </Show>
    </section>
  )
}
